import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button, Select, Modal,
} from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { useMutation, useQueryClient } from 'react-query';
import moment from 'moment';
import { useMediaQuery } from 'react-responsive';

import StepPlatformType from './StepPlatformType';
import StepDeploymentLocation from './StepDeploymentLocation';
import StepEventDate from './StepEventDate';
import { COLLECTION_STATUSES } from './constants';
import ModalContent from './ModalContent';
import { postPlatformEvent, postDeploymentSite, putPlatform } from '../../../../services/dataset.service';
import EventDatePicker from './EventDatePicker';
import MapQueryTypes from '../../../../services/query-types/map';

const Deployment = (props) => {
  const { obsDatasetSummary } = props;

  const isMobile = useMediaQuery({ maxWidth: 767 });
  const queryClient = useQueryClient();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [selectedStatus, setSelectedStatus] = useState('');
  const [platformType, setPlatformType] = useState(obsDatasetSummary?.obs_dataset_platform_assignment?.platform?.platform_type);
  const [deploymentSite, setDeploymentSite] = useState(obsDatasetSummary?.deployment_site);
  const [eventDate, setEventDate] = useState();

  const statusMutation = useMutation(
    ({ selectedStatus, eventDate }) => {
      const collectionStatus = COLLECTION_STATUSES.find((status) => status.value === selectedStatus);
      const payload = {
        event: selectedStatus,
        collection_status: collectionStatus.collection_status,
        event_time: moment(eventDate).utc().toISOString(),
        description: collectionStatus.description,
      };

      return postPlatformEvent(obsDatasetSummary?.obs_dataset_platform_assignment.platform.platform_id, payload);
    },
    {
      onSettled: () => {
        queryClient.invalidateQueries(['platformEvents', obsDatasetSummary?.obs_dataset_platform_assignment.platform.platform_id]);
        queryClient.invalidateQueries(MapQueryTypes.REST_OBS_DATASET_SUMMARIES_DATASET_DETAIL);
        queryClient.invalidateQueries(MapQueryTypes.REST_OBS_DATASET_SUMMARIES);
        // TODO: use the same cache key for summaries everywhere?
      },
    },
  );

  const deploymentSiteMutation = useMutation(
    (deploymentSite) => {
      const payload = {
        latitude: deploymentSite.latitude,
        longitude: deploymentSite.longitude,
      };

      return postDeploymentSite(obsDatasetSummary?.obs_dataset_id, payload);
    },
    {
      onSettled: () => {
        queryClient.invalidateQueries(MapQueryTypes.REST_OBS_DATASET_SUMMARIES_DATASET_DETAIL);
        queryClient.invalidateQueries(MapQueryTypes.REST_OBS_DATASET_SUMMARIES);
        // TODO: use the same cache key for summaries everywhere?
      },
    },
  );

  const platformMutation = useMutation(
    (platformType) => {
      const platform = obsDatasetSummary?.obs_dataset_platform_assignment?.platform;
      // v3 PUT /platforms/{id} replaces external_id/external_id_type/webcam_url wholesale
      // (no exclude_none merge like PUT /obs-datasets/{id}), so they must be resent here
      // or a platform_type-only update will silently null them out.
      const payload = {
        platform_type: platformType,
        external_id: platform?.external_id,
        external_id_type: platform?.external_id_type,
        webcam_url: platform?.webcam_url,
      };
      return putPlatform(platform?.platform_id, payload);
    },
    {
      onSettled: () => {
        queryClient.invalidateQueries(MapQueryTypes.REST_OBS_DATASET_SUMMARIES_DATASET_DETAIL);
        queryClient.invalidateQueries(MapQueryTypes.REST_OBS_DATASET_SUMMARIES);
        // TODO: use the same cache key for summaries everywhere?
      },
    },
  );

  const handleSave = () => {
    // only send if changed
    if (selectedStatus !== obsDatasetSummary?.obs_dataset_platform_assignment?.platform?.platform_event?.collection_status) {
      statusMutation.mutate({
        selectedStatus,
        eventDate,
      });
    }

    if (deploymentSite?.latitude !== obsDatasetSummary?.deployment_site?.latitude
      || deploymentSite?.longitude !== obsDatasetSummary?.deployment_site?.longitude) {
      deploymentSiteMutation.mutate({
        ...deploymentSite,
      });
    }

    if (platformType !== obsDatasetSummary?.obs_dataset_platform_assignment?.platform?.platform_type) {
      platformMutation.mutate(platformType);
    }
  };

  const totalSteps = selectedStatus === 'activated' ? 4 : 2;

  const onClickConfigure = () => {
    setStep(1);
    setIsModalOpen(true);
  };

  const handleOk = () => {
    setIsModalOpen(false);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
  };

  const onClickNext = () => {
    if (step === totalSteps - 1) {
      handleSave();
    }
    if (step === totalSteps) {
      setIsModalOpen(false);
    }
    setStep(step + 1);
  };

  const onClickBack = () => {
    setStep(step - 1);
  };

  const progressButtonText = () => {
    if (step === totalSteps) {
      return 'Close';
    }

    if (step === totalSteps - 1) {
      return 'Confirm';
    }

    return 'Next';
  };

  useEffect(() => {
    // seems unnecessary, but since these are state variables, we need to update when obsDatasetSummary changes
    // will not update automatically even though obsDatasetSummary is a prop, since they may be initialized before
    // obsDatasetSummary is available
    setPlatformType(obsDatasetSummary?.obs_dataset_platform_assignment?.platform?.platform_type);
    setDeploymentSite(obsDatasetSummary?.deployment_site);
  }, [obsDatasetSummary]);

  // TODO: put this language in COLLECTION_STATUSES for a more declarative approach?
  const modalTile = () => {
    if (selectedStatus === 'activated') {
      if (step === 4) {
        return 'Deployment recorded successfully.';
      }

      return 'Launch Platform';
    }

    if (selectedStatus === 'decommissioned') {
      return 'Decommission Platform';
    }

    if (selectedStatus === 'recovered') {
      return 'Recover Platform';
    }

    if (selectedStatus === 'testing') {
      return 'Testing Mode';
    }
  };

  return (
    <div>
      <Select
        onSelect={(value) => setSelectedStatus(value)}
        placeholder="Select..."
        options={COLLECTION_STATUSES}
        style={{
          width: '100%',
        }}
      />
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          marginTop: 10,
        }}
      >
        <Button
          className="configure-button"
          onClick={onClickConfigure}
          disabled={!selectedStatus}
        >
          Configure
        </Button>
        <Modal
          className="platform-management-modal"
          title={modalTile()}
          visible={isModalOpen}
          onOk={handleOk}
          footer={null}
          onCancel={handleCancel}
          closeIcon={<CloseOutlined color="white" />}
          style={isMobile ? { top: 0, paddingBottom: 0 } : undefined}
          width={isMobile ? '100%' : undefined}
        >
          <div className="step-scroll-content">
            {selectedStatus === 'activated' && (
              <>
                <div className="steps">
                  {Array.from({ length: totalSteps }, (_, i) => (
                    <div key={i} className={`step${step >= i + 1 ? ' active' : ''}`} />
                  ))}
                </div>
                {step === 1 && (
                  <StepPlatformType
                    previousPlatformType={obsDatasetSummary?.obs_dataset_platform_assignment?.platform?.platform_type}
                    platformType={platformType}
                    setPlatformType={setPlatformType}
                  />
                )}
                {step === 2 && (
                  <StepDeploymentLocation
                    deploymentSite={deploymentSite}
                    setDeploymentSite={setDeploymentSite}
                  />
                )}
                {step === 3 && (
                  <StepEventDate
                    eventDate={eventDate}
                    setEventDate={setEventDate}
                  />
                )}
                {step === 4 && (
                  <ModalContent
                    heading="All provided information has been saved. Your platform is now active and ready for reporting."
                    body="Note: The platform may appear offline until it transmits its first data. This may take some time depending on its connection schedule or transmission frequency."
                  />
                )}
              </>
            )}
            {selectedStatus === 'decommissioned' && (
              <>
                {step === 1 && (
                  <div>
                    <ModalContent
                      heading="Are you sure you want to decommission this platform?"
                      body="Decommissioning will mark the platform as offline and end its active deployment. If so, please enter the planned decommission date to complete this action."
                    />
                    <EventDatePicker eventDate={eventDate} setEventDate={setEventDate} />
                  </div>
                )}
                {step === 2 && (
                  <div>
                    <ModalContent
                      heading="Platform decommission saved."
                      body="The platform will be marked offline on the selected date, and its deployment record has been updated."
                    />
                  </div>
                )}
              </>
            )}
            {selectedStatus === 'recovered' && (
              <>
                {step === 1 && (
                  <div>
                    <ModalContent
                      heading="Are you sure you want to recover this platform?"
                      body="Recovering will mark the platform as offline and end its active deployment. If so, please enter the planned recovery date to complete this action."
                    />
                    <EventDatePicker eventDate={eventDate} setEventDate={setEventDate} />
                  </div>
                )}
                {step === 2 && (
                  <ModalContent
                    heading="Platform recovery saved."
                    body="The platform will be marked offline on the selected date, and its deployment record has been updated."
                  />
                )}
              </>
            )}
            {selectedStatus === 'testing' && (
              <>
                {step === 1 && (
                  <div>
                    <ModalContent
                      heading="Are you sure you want to place this platform in Testing Mode?"
                      body="Use testing mode to verify sensors, connections, and data flow before official deployment. While in testing mode, the platform will not appear as actively deployed and incoming data will be flagged accordingly."
                    />
                    <EventDatePicker eventDate={eventDate} setEventDate={setEventDate} />
                  </div>
                )}
                {step === 2 && (
                  <ModalContent
                    heading="Platform is now in Testing Mode."
                    body="The platform will be marked offline on the selected date, and its deployment record has been updated."
                  />
                )}
              </>
            )}
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: step === totalSteps ? 'center' : 'space-between',
              marginTop: 20,
            }}
          >
            {step > 1 && step < totalSteps && (
              <Button
                type="link"
                className="link-button white"
                onClick={onClickBack}
              >
                Back
              </Button>
            )}
            {step === 1 && (
              <Button
                type="link"
                className="link-button white"
                onClick={handleCancel}
              >
                Cancel
              </Button>
            )}
            <Button
              className="seagull-button"
              onClick={onClickNext}
            >
              {progressButtonText()}
            </Button>
          </div>
        </Modal>
      </div>
    </div>
  );
};

Deployment.propTypes = {
  obsDatasetSummary: PropTypes.object,
};

Deployment.defaultProps = {
  obsDatasetSummary: {},
};

export default Deployment;
